import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CaretLeft } from "@phosphor-icons/react";
import { LegalHeader, LegalFooter, Section } from "@/pages/Impressum";

/**
 * Allgemeine Geschäfts- und Nutzungsbedingungen (AGB) für Bodenblick.
 * Österreichisches Recht anwendbar. Während der Testphase ausschließlich
 * unentgeltliche Nutzung; ersetzt später die Pro-Subscription-AGB.
 */
export default function AGB() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] relative z-10" data-testid="agb-root">
      <Helmet>
        <title>Nutzungsbedingungen · Bodenblick</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <LegalHeader />

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white"
          data-testid="agb-back"
        >
          <CaretLeft size={14} /> Zurück zur Startseite
        </Link>

        <div>
          <div className="eid-label">Rechtliches</div>
          <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">
            Allgemeine Nutzungsbedingungen
          </h1>
          <p className="text-sm text-zinc-400 mt-2">
            Diese Bedingungen regeln die Nutzung der Web-Anwendung Bodenblick
            (bodenblick.com). Anbieterin ist Johanna Maislinger (siehe{" "}
            <Link to="/impressum" className="underline">
              Impressum
            </Link>
            ).
          </p>
        </div>

        <Section title="1. Geltungsbereich">
          <p>
            Diese Nutzungsbedingungen gelten für sämtliche Zugriffe auf und
            Nutzungen der Website bodenblick.com sowie der Web-Anwendung
            (gemeinsam: „Dienst"). Mit Nutzung des Dienstes stimmst du diesen
            Bedingungen zu.
          </p>
        </Section>

        <Section title="2. Leistungsbeschreibung · Testphase">
          <p>
            Der Dienst befindet sich in einer geschlossenen Testphase und wird
            derzeit <strong>unentgeltlich</strong> bereitgestellt. Leistungs­umfang,
            Verfügbarkeit und Features können jederzeit ohne Vorankündigung
            geändert oder eingestellt werden. Ein Anspruch auf Verfügbarkeit oder
            eine bestimmte Funktion besteht während der Testphase nicht.
          </p>
          <p>
            Kernleistung ist die Darstellung aggregierter und anonymisierter
            Daten aus Satelliten- (Copernicus), Wetter- (Open-Meteo) und
            Finanzmarkt-Quellen sowie daraus abgeleiteter Handlungsempfehlungen
            auf Basis der Region und Kultur.
          </p>
        </Section>

        <Section title="3. Registrierung und Newsletter">
          <p>
            Die Nutzung des geschützten Cockpits setzt eine Anmeldung voraus.
            Der Zugang erfolgt während der Testphase ausschließlich auf
            Einladung. Für den öffentlichen Newsletter genügt die Eintragung
            per Double-Opt-In; das Einverständnis kann jederzeit mit Wirkung
            für die Zukunft widerrufen werden.
          </p>
        </Section>

        <Section title="4. Datenqualität · Haftungsausschluss">
          <p>
            Die dargestellten Werte sind statistische Schätzungen, abgeleitet
            aus Fernerkundungs- und Wettermodellen. Sie ersetzen keine
            fachliche Vor-Ort-Beurteilung und sind keine Anlage-, Rechts- oder
            agronomische Beratung.
          </p>
          <p>
            Für Richtigkeit, Vollständigkeit, Aktualität oder Verfügbarkeit der
            bereitgestellten Daten und Empfehlungen wird keine Gewähr
            übernommen, soweit dies gesetzlich zulässig ist. Eine Haftung für
            Schäden aus der Nutzung oder Nichtnutzung des Dienstes ist — außer
            bei Vorsatz, grober Fahrlässigkeit oder Verletzung wesentlicher
            Vertragspflichten — ausgeschlossen. Eine Haftung für mittelbare
            Schäden, entgangenen Gewinn oder Ertragsausfälle ist ebenfalls
            ausgeschlossen. Zwingende gesetzliche Haftungstatbestände
            (insbesondere nach dem Produkthaftungsgesetz) bleiben unberührt.
          </p>
        </Section>

        <Section title="4a. Marktdaten · Keine Anlageempfehlung">
          <p>
            <strong>
              Preis-, Kurs- und Prognosedaten zu Rohstoffen (Weizen, Mais,
              Soja, Raps, Hafer etc.) dienen ausschließlich Informations­zwecken
              und stellen KEINE Kauf-, Verkaufs- oder Halteempfehlung dar.
            </strong>{" "}
            Bodenblick ist kein Wertpapierdienstleister, keine Anlageberatung
            und kein Vermögensverwalter im Sinne des WAG 2018 / MiFID II.
          </p>
          <p>
            Die gezeigten Forecasts sind rein statistische Hochrechnungen aus
            vergangenen End-of-Day-Kursen (Quelle: yfinance/Alpha Vantage).
            Finanzmärkte sind hochvolatil; vergangene Entwicklungen sind kein
            verlässlicher Indikator für zukünftige Preise. Jede Handels- oder
            Vermarktungsentscheidung musst du eigenverantwortlich und ggf.
            nach Rücksprache mit einem qualifizierten Berater treffen.
          </p>
          <p>
            Für finanzielle Verluste, entgangene Gewinne, Fehlentscheidungen
            bei Terminkontrakten oder Vermarktung — gleich aus welchem Grund —
            wird eine Haftung ausgeschlossen, soweit dies gesetzlich zulässig
            ist.
          </p>
        </Section>

        <Section title="4b. Futter- und Tiergesundheits-Hinweise">
          <p>
            Die im Modul „Weide &amp; Futter" bereitgestellten Werte (Rohprotein,
            NEL, Fruktan-Risiko, Biomasse-Index) sind Schätzwerte aus
            Fernerkundungs- und Wetterdaten nach veröffentlichten
            Literatur­werten (u. a. LfL-Weidefutter­monitoring, Longland &amp;
            Byrd 2006). Sie <strong>ersetzen keine Labor-Futtermittel­analyse</strong>{" "}
            und sind keine tierärztliche oder fütterungs­medizinische Beratung.
          </p>
          <p>
            Entscheidungen zur Fütterung, zum Weidegang (insb. bei hufrehe­gefährdeten
            Pferden) oder zur Tiergesundheit sind ausschließlich auf Basis
            einer fachlichen Vor-Ort-Einschätzung, einer Laboranalyse und/oder
            in Abstimmung mit einem/einer Tierarzt/Tierärztin zu treffen.
          </p>
        </Section>

        <Section title="5. Nutzungsrechte &amp; Urheberrecht">
          <p>
            Alle Inhalte, Grafiken, Algorithmen, Texte und Gestaltungs­elemente
            sind urheberrechtlich geschützt. Du erhältst ein einfaches,
            nicht-übertragbares Recht, den Dienst im vertrags­gemäßen Umfang
            für eigene — nicht kommerzielle — Zwecke zu nutzen. Weitergabe,
            Vervielfältigung, Reverse Engineering, automatisiertes Auslesen
            (Scraping) oder gewerbliche Weiterverwertung sind ohne vorherige
            schriftliche Zustimmung unzulässig.
          </p>
          <p>
            Satelliten­daten stammen vom Copernicus-Programm der EU
            (© ESA/EU, Copernicus-Lizenzbestimmungen), Wetterdaten von
            Open-Meteo (CC-BY 4.0). Finanzkurse stammen von öffentlichen
            End-of-Day-Quellen.
          </p>
        </Section>

        <Section title="6. Exportierte Social-Media-Inhalte">
          <p>
            Über die Export-Funktion erstellte Grafiken dürfen privat und
            nicht-gewerblich auf Social-Media-Plattformen geteilt werden.
            Eine Bearbeitung der anonymisierten Regions-Labels („Feld A" etc.)
            zur Identifizierung konkreter Flurstücke widerspricht der Privacy-
            Policy und ist untersagt.
          </p>
        </Section>

        <Section title="7. Pflichten der Nutzer:innen">
          <p>
            Du verpflichtest dich, keine Maßnahmen zu ergreifen, die die
            technische Funktion des Dienstes beeinträchtigen (z. B. Lasttests,
            DDoS, Manipulation von Anfragen), keine Schadsoftware einzubringen
            und keine rechtswidrigen Inhalte bereitzustellen.
          </p>
        </Section>

        <Section title="8. Vertragsdauer · Kündigung">
          <p>
            Das Newsletter-Abonnement kann jederzeit durch Klick auf den
            Abmelde-Link in jeder Mail oder formlos per E-Mail an
            vision@bodenblick.com beendet werden. Registrierte Konten werden
            auf Anfrage vollständig gelöscht.
          </p>
        </Section>

        <Section title="9. Änderungen der Bedingungen">
          <p>
            Wir behalten uns vor, diese Bedingungen anzupassen, wenn sich die
            Leistungen oder die Rechtslage ändern. Die jeweils aktuelle
            Fassung ist unter bodenblick.com/agb abrufbar. Bestehende
            Nutzer:innen werden über wesentliche Änderungen per E-Mail bzw.
            in der Anwendung informiert.
          </p>
        </Section>

        <Section title="10. Anwendbares Recht &amp; Gerichtsstand">
          <p>
            Es gilt österreichisches Recht unter Ausschluss des UN-Kaufrechts.
            Gerichts­stand für Streitigkeiten mit Unternehmer:innen ist das
            sachlich zuständige Gericht am Sitz der Anbieterin (Bad Ischl /
            Gmunden). Gegenüber Verbraucher:innen bleiben zwingende
            Verbraucher­gerichtsstände unberührt.
          </p>
        </Section>

        <Section title="11. Salvatorische Klausel">
          <p>
            Sollten einzelne Bestimmungen unwirksam sein oder werden, so
            berührt dies die Wirksamkeit der übrigen Bestimmungen nicht. An
            die Stelle der unwirksamen Bestimmung tritt diejenige wirksame
            Regelung, die dem wirtschaftlichen Zweck am nächsten kommt.
          </p>
        </Section>

        <div className="pt-4 border-t border-white/10 text-xs text-zinc-500 font-mono">
          Stand: {new Date().toLocaleDateString("de-AT", { year: "numeric", month: "long" })}
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
